"use client"
import { useEffect, useState } from "react"
import authService from '../services/authService'
import { PulseLogo } from './ui/pulse-logo'

interface MicrosoftLoginProps {
  onLoginSuccess?: () => void;
}

export default function MicrosoftLogin({ onLoginSuccess }: MicrosoftLoginProps) {
  const [, setPulse] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const interval = setInterval(() => {
      setPulse((prev) => (prev + 1) % 100)
    }, 50)
    return () => clearInterval(interval)
  }, [])

  const handleMicrosoftLogin = () => {
    // Redirect to Microsoft authentication page (legacy support)
    authService.loginWithMicrosoft();
  }

  const handleUsernamePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    
    try {
      const result = await authService.loginWithCredentials({ username, password });
      if (result.success) {
        setShowLoginForm(false)
        setUsername("")
        setPassword("")
        
        // Call the success callback to update parent component
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        console.error('Login failed:', result.error);
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error)
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Theme-aware gradient overlay */}
      <div
        className="absolute top-0 left-0 right-0 h-96 opacity-30"
        style={{
          background: `linear-gradient(135deg, 
            #ff8697 0%, 
            #ff6b85 25%, 
            #ff4d73 50%, 
            #00A9A7 75%, 
            transparent 100%)`,
        }}
      />
      <div
        className="absolute top-0 left-0 right-0 h-64 opacity-20"
        style={{
          background: `radial-gradient(ellipse at top, 
            #ff8697 0%, 
            #00A9A7 40%, 
            transparent 70%)`,
        }}
      />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(var(--text-secondary) 1px, transparent 1px),
            linear-gradient(90deg, var(--text-secondary) 1px, transparent 1px)
          `,
          backgroundSize: "30px 30px",
        }}
      />

      {/* Floating data elements with theme colors */}
      <div className="absolute top-20 left-10 text-sm font-mono" style={{ color: "#ff8697" }}>
        CPU: 45.2%
      </div>
      <div className="absolute top-32 right-16 text-sm font-mono" style={{ color: "#00A9A7" }}>
        Memory: 2.1GB
      </div>
      <div className="absolute top-40 left-1/4 text-sm font-mono" style={{ color: "#00A9A7" }}>
        Services: 24/25
      </div>
      <div className="absolute top-28 right-1/3 text-sm font-mono" style={{ color: "#ff8697" }}>
        Uptime: 99.9%
      </div>
      <div className="absolute top-52 left-1/3 text-sm font-mono" style={{ color: "#00A9A7" }}>
        Latency: 12ms
      </div>

      {/* Login Buttons Container */}
      <div className="absolute top-6 right-6 z-20 space-y-3">
        {/* Microsoft Login Card */}
        <div
          className="relative p-3 rounded-xl cursor-pointer transition-all duration-300 hover:scale-105"
          style={{
            background: `linear-gradient(135deg, 
              rgba(255, 134, 151, 0.15) 0%, 
              rgba(0, 169, 167, 0.15) 50%, 
              rgba(255, 134, 151, 0.15) 100%)`,
            backdropFilter: "blur(20px)",
            border: "1px solid var(--border-color)",
            boxShadow: isHovered
              ? "0 12px 24px rgba(255, 134, 151, 0.2), 0 0 0 1px rgba(0, 169, 167, 0.3)"
              : "0 4px 16px rgba(0, 0, 0, 0.3)",
          }}
          onClick={handleMicrosoftLogin}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Microsoft Logo */}
          <div className="flex items-center gap-2 mb-2">
            <div className="grid grid-cols-2 gap-0.5 w-4 h-4">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-sm"></div>
              <div className="w-1.5 h-1.5 bg-green-500 rounded-sm"></div>
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-sm"></div>
              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-sm"></div>
            </div>
            <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
              Microsoft
            </span>
          </div>

          {/* Login Text */}
          <div className="text-center">
            <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
              Enterprise Access
            </div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.8 }}>
              SSO
            </div>
          </div>

          {/* Animated Border */}
          <div
            className="absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300"
            style={{
              background: `linear-gradient(45deg, 
                #ff8697, #00A9A7, #ff6b85, #ff8697)`,
              backgroundSize: "300% 300%",
              animation: isHovered ? "gradient-shift 2s ease infinite" : "none",
              mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              maskComposite: "xor",
              padding: "1px",
            }}
          />
        </div>

        {/* Username/Password Login Button */}
        <div
          className="relative p-3 rounded-xl cursor-pointer transition-all duration-300 hover:scale-105"
          style={{
            background: `linear-gradient(135deg, 
              rgba(0, 169, 167, 0.15) 0%, 
              rgba(255, 134, 151, 0.15) 50%, 
              rgba(0, 169, 167, 0.15) 100%)`,
            backdropFilter: "blur(20px)",
            border: "1px solid var(--border-color)",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
          }}
          onClick={() => setShowLoginForm(true)}
        >
          {/* Login Icon */}
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: "#00A9A7" }}>
              <div style={{ color: "#ffffff" }} className="font-bold text-xs">
                U
              </div>
            </div>
            <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
              Username
            </span>
          </div>

          {/* Login Text */}
          <div className="text-center">
            <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
              Local Access
            </div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.8 }}>
              Token Auth
            </div>
          </div>
        </div>
      </div>

      {/* Login Form Modal */}
      {showLoginForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}>
          <div
            className="relative p-6 rounded-xl w-full max-w-md"
            style={{
              background: `linear-gradient(135deg, 
                var(--bg-secondary) 0%, 
                var(--bg-tertiary) 100%)`,
              backdropFilter: "blur(20px)",
              border: "1px solid var(--border-color)",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.5)",
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setShowLoginForm(false);
                setError(null);
                setUsername("");
                setPassword("");
              }}
              className="absolute top-4 right-4 text-xl hover:opacity-70 transition-opacity"
              style={{ color: 'var(--text-primary)' }}
            >
              ×
            </button>

            {/* Form Header */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                Login
              </h2>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Enter your credentials to access the dashboard
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: "rgba(255, 134, 151, 0.1)", border: "1px solid rgba(255, 134, 151, 0.3)" }}>
                <p className="text-sm" style={{ color: "#ff8697" }}>
                  {error}
                </p>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleUsernamePasswordLogin} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full p-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                  required
                  disabled={isLoading}
                />
              </div>
              <div>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                  required
                  disabled={isLoading}
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full p-3 rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50"
                style={{
                  background: `linear-gradient(135deg, #00A9A7 0%, #008a87 50%, #006b69 100%)`,
                  color: "#ffffff",
                }}
              >
                {isLoading ? "Logging in..." : "Login"}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center space-y-12">
          {/* Enhanced Pulse Animation with new logo */}
          <div className="relative flex justify-center">
            <PulseLogo size="lg" showText={false} className="transform scale-150" />
          </div>

          <div>
            <h1 className="text-6xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Welcome to{" "}
              <span className="font-bold" style={{ color: "#00A9A7" }}>
                Pulse
              </span>
            </h1>
            <p className="text-xl font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
              On-Premises Service Monitoring Platform
            </p>
            <p className="text-lg font-medium" style={{ color: 'var(--text-secondary)' }}>
              Real-time data aggregation • Service health monitoring • Enterprise insights
            </p>
          </div>

          {/* Enhanced stats preview with theme colors */}
          <div className="grid grid-cols-3 gap-12 mt-16">
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: "rgba(0, 169, 167, 0.1)" }}>
              <div className="text-3xl font-bold mb-2" style={{ color: "#00A9A7" }}>
                24/7
              </div>
              <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Monitoring
              </div>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: "rgba(255, 134, 151, 0.1)" }}>
              <div className="text-3xl font-bold mb-2" style={{ color: "#ff8697" }}>
                99.9%
              </div>
              <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Uptime
              </div>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: "rgba(0, 169, 167, 0.1)" }}>
              <div className="text-3xl font-bold mb-2" style={{ color: "#00A9A7" }}>
                Real-time
              </div>
              <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Analytics
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  )
}
